import { useEffect, type RefObject } from 'react';
import { appDataStore } from '../stores/appData';

/**
 * Hands the founder's preview token to the prototype running in the preview, and tells it when
 * the startup's live data changed (see `stores/appData.ts`). Messages are only answered when they
 * come from this iframe's own window, and only sent to it.
 */
export function useAppDataBridge(iframeRef: RefObject<HTMLIFrameElement | null>): void {
  useEffect(() => {
    let asked = false;

    const send = (message: unknown) => {
      iframeRef.current?.contentWindow?.postMessage(message, '*');
    };
    const sendConfig = () => {
      const config = appDataStore.config.get();
      // Still being fetched: answer once it arrives (the subscription below).
      if (config === undefined) return;
      send({ type: 'ideoz:app-data:config', config });
    };

    const onMessage = (event: MessageEvent) => {
      const own = iframeRef.current?.contentWindow;
      if (!own || event.source !== own) return;
      const data = event.data as { type?: unknown } | null;
      if (data?.type !== 'ideoz:app-data:request') return;
      asked = true;
      sendConfig();
    };
    window.addEventListener('message', onMessage);

    const stopConfig = appDataStore.config.listen(() => {
      if (!asked) return;
      sendConfig();
      // A preview that gave up waiting kept its samples; now it can read the live data.
      send({ type: 'ideoz:app-data:changed', entity: null });
    });
    const stopChanged = appDataStore.changed.listen((change) => {
      if (change) send({ type: 'ideoz:app-data:changed', entity: change.entity });
    });

    return () => {
      window.removeEventListener('message', onMessage);
      stopConfig();
      stopChanged();
    };
  }, [iframeRef]);
}
